import { IconButton } from "@/design-system/IconButton/IconButton";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { X } from "lucide-react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

export const StickySidebar = ({
  children,
  handleClose,
  title,
  extraTopContent,
  subtitle,
  action,
}: {
  children: React.ReactNode;
  handleClose: () => void;
  /** Without it the panel draws no header (e.g. a sheet with its own). */
  title?: string;
  extraTopContent?: React.ReactNode;
  /** A line under the title. */
  subtitle?: string;
  /** A header action (Apply); moves the close to the left, as in the app. */
  action?: React.ReactNode;
}) => {
  const { t } = useTranslation("common");
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  useScrollLock(isTouchDevice);

  const topToPage = useSelector(topToPageSelector);

  return (
    <div
      className={cn(
        "fixed right-0 bottom-0 z-50 flex w-screen flex-col border-l border-edge-strong bg-background shadow-2xl mobile:w-[380px]",
        isTouchDevice
          ? "top-0"
          : cn(
              "right-[max(0px,calc((100%-2132px)/2))]",
              topToPage.tailwindString,
            ),
      )}
    >
      {title && (
        <div className="flex h-16 shrink-0 items-center gap-x-3 border-b border-edge-strong px-4">
          {action && (
            <IconButton
              icon={<X className="size-4" />}
              shape="round"
              aria-label={t("close")}
              onClick={handleClose}
            />
          )}
          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col",
              action && "items-center text-center",
            )}
          >
            <p className="text-base font-bold text-foreground">{title}</p>
            {subtitle && (
              <p className="truncate text-xs text-foreground-muted">
                {subtitle}
              </p>
            )}
          </div>
          {action ?? (
            <IconButton
              icon={<X className="size-4" />}
              shape="round"
              aria-label={t("close")}
              onClick={handleClose}
            />
          )}
        </div>
      )}
      {extraTopContent}
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
};
